/**
 * Auth Client
 *
 * Main entry point for authentication in the application.
 * Provides a clean API that abstracts away the underlying provider.
 */

import { getAuthProvider } from './factory';
import type {
  AuthUser,
  AuthSession,
  AuthError,
  SignInResult,
  SignUpResult,
  AuthStateChangeCallback,
  ResetPasswordOptions,
} from './types';

export const authClient = {
  /**
   * Sign in with email and password
   */
  async signIn(email: string, password: string): Promise<SignInResult> {
    const provider = getAuthProvider();
    return provider.signInWithPassword(email, password);
  },

  /**
   * Sign up with email and password
   */
  async signUp(email: string, password: string): Promise<SignUpResult> {
    const provider = getAuthProvider();
    return provider.signUp(email, password);
  },

  /**
   * Sign out the current user
   */
  async signOut(): Promise<{ error: AuthError | null }> {
    const provider = getAuthProvider();
    return provider.signOut();
  },

  /**
   * Get the current session
   */
  async getSession(): Promise<{ session: AuthSession | null; error: AuthError | null }> {
    const provider = getAuthProvider();
    return provider.getSession();
  },

  /**
   * Refresh the current session
   */
  async refreshSession(): Promise<{ session: AuthSession | null; error: AuthError | null }> {
    const provider = getAuthProvider();
    return provider.refreshSession();
  },

  /**
   * Subscribe to auth state changes
   * Returns an unsubscribe function
   */
  onAuthStateChange(callback: AuthStateChangeCallback): () => void {
    const provider = getAuthProvider();
    return provider.onAuthStateChange(callback);
  },

  /**
   * Get the current user (synchronous if cached)
   */
  getCurrentUser(): AuthUser | null {
    const provider = getAuthProvider();
    return provider.getCurrentUser();
  },

  /**
   * Send password reset email.
   * Pass `options.redirectTo` to say where the emailed link should land, so a
   * reset requested from localhost or a preview build comes back there.
   */
  async resetPassword(
    email: string,
    options?: ResetPasswordOptions,
  ): Promise<{ error: AuthError | null }> {
    const provider = getAuthProvider();
    if (provider.resetPasswordForEmail) {
      return provider.resetPasswordForEmail(email, options);
    }
    return { error: { message: 'Password reset not supported by this provider' } };
  },

  /**
   * Update user data
   */
  async updateUser(data: {
    email?: string;
    password?: string;
    metadata?: Record<string, unknown>;
  }): Promise<{ user: AuthUser | null; error: AuthError | null }> {
    const provider = getAuthProvider();
    if (provider.updateUser) {
      return provider.updateUser(data);
    }
    return { user: null, error: { message: 'User update not supported by this provider' } };
  },
};

// Re-export types for convenience
export type { AuthUser, AuthSession, AuthError, SignInResult, SignUpResult, AuthStateChangeCallback, ResetPasswordOptions };
